import React from "react";

import { ColumnDef } from "@tanstack/react-table";
import { ZapIcon } from "lucide-react";

import { UserAvatar } from "@/components/common/user-avatar";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { Badge } from "@/components/ui/badge";
import { makeTooltip } from "@/components/ui/tooltip";
import { useMembers } from "@/contexts/member-context";
import { parseUnixTimestamp } from "@/lib/dateUtils";
import { IShelfData } from "@/types/shelf";

export const useShelvesColumns = () => {
  const { members } = useMembers();
  const columns = React.useMemo<ColumnDef<IShelfData>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: "ID",
      },
      {
        id: "name",
        // accessorKey: "document.name",
        accessorFn: (row) => row.document.name,
        header: "Name",
        minSize: 100,
        cell: function render({ getValue }: any) {
          return makeTooltip(
            <div
              className="truncate"
              dangerouslySetInnerHTML={{ __html: getValue() }}
            ></div>,
          );
        },
        enableSorting: false,
        size: 100,
      },
      {
        id: "position",
        header: "Position",
        accessorKey: "document.position",
        enableColumnFilter: false,
      },
      {
        id: "variant",
        header: "Variant",
        accessorKey: "document.variant",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "section",
        header: "Section",
        accessorFn: (row) => row.document.section ?? "Music",
        enableSorting: false,
        size: 90,
        meta: {
          filterType: "select",
          options: [
            { label: "Music", value: "Music" },
            { label: "Cinema", value: "Cinema" },
            { label: "Theater", value: "Theater" },
          ],
        },
      },
      {
        id: "isQuickAction",
        header: "Quick Action",
        accessorFn: (row) => row.document.isQuickAction ?? false,
        enableSorting: false,
        size: 120,
        cell: function render({ row, getValue }: any) {
          const icon = row.original.document.quickActionIcon?.name as
            | string
            | undefined;
          return getValue() ? (
            makeTooltip(
              <Badge variant="default" className="gap-1">
                <ZapIcon className="size-3" />
                Quick
              </Badge>,
              icon ? `Icon: ${icon}` : undefined,
            )
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
        meta: {
          filterType: "select",
          options: [
            { label: "Quick action", value: "true" },
            { label: "Regular", value: "false" },
          ],
        },
      },
      {
        id: "hide",
        accessorKey: "document.hide",
        header: "Hide",
        enableSorting: false,
        size: 60,
      },
      {
        id: "context",
        accessorFn: (row) => row?.document?.context?.join(", "),
        cell: function render({ getValue }: any) {
          return makeTooltip(
            <div className="font-bold truncate">{getValue()}</div>,
          );
        },
        header: "Context",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "createdBy",
        header: "Created By",
        minSize: 170,
        accessorKey: "document.createdBy",
        cell: function render({ getValue }: any) {
          let value = getValue();
          // console.log("members", members, value);
          const member = members.find(
            (x) =>
              x.id === value || x.adminMetadata?.relatedIds?.includes(value),
          );

          return member ? (
            <div className="p-1">
              <UserAvatar {...member} half />
            </div>
          ) : (
            value
          );
        },
        enableSorting: true,
      },
      {
        id: "createdAt",
        header: "Created At",
        accessorFn: (row) => parseUnixTimestamp(row.document.createdAt),
        cell: function render({ getValue }: any) {
          return makeTooltip(
            getValue().format("DD/MM/YYYY HH:mm"),
            getValue().format("ddd, MMM D, YYYY HH:mm"),
          );
        },
        enableColumnFilter: true,
        meta: {
          filterType: "date",
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: function render({ row }) {
          return (
            <EditButton
              size="sm"
              hideText={true}
              recordItemId={row.original.document.id}
            />
          );
        },
        enableSorting: false,
      },
    ],
    [members],
  );

  return columns;
};
